import { ReactElement, useState } from "react";
import { useMatch, useSearchParams } from "react-router";
import { twMerge } from "tailwind-merge";
import { useLocalStorage, useMediaQuery } from "usehooks-ts";
import {
  Icon,
  ProfilePicture,
  SpinnerLegacy,
} from "../../../../../../components";
import {
  Dialog,
  DialogContent,
} from "../../../../../../components/modal/Dialog";
import {
  ProfilePictureCollection,
  ProfilePictureCollectionImage,
} from "../../../../../../components/profilePictureCollection/profilePictureCollection";
import { ORDER_BUYER } from "../../../../../../constants/routes";
import { useSellarUser } from "../../../../../../hooks";
import { useSupplier } from "../../../../../../hooks/api/v2/supplier/useSupplier";
import { useSupplierStorefrontV2 } from "../../../../../../hooks/api/v2/supplier/useSupplierStorefrontV2";
import {
  SignupFlowStep,
  useSignupContext,
} from "../../../../../../hooks/useSignupContext.shared";
import { UserSignupFlow } from "../../../../../auth/signIn/UserSignupFlow";
import { StorefrontSignupDialogBasketInfo } from "./StorefrontSignupDialogBasketInfo";
import { StorefrontSignupDialogSupplierInfo } from "./StorefrontSignupDialogSupplierInfo";
import { useDiscoveryV2SearchStore } from "../../../../../discovery/v2/DiscoveryV2SearchStore";
import { useShallow } from "zustand/react/shallow";

export const StorefrontSignupDialog = ({
  active,
}: {
  active: boolean;
}): ReactElement => {
  // required for capturing supplierId during market user sign ups
  const { supplierId } = useDiscoveryV2SearchStore(
    useShallow((state) => ({
      supplierId: state.supplierId,
    })),
  );

  // The dialog is mounted at the app shell, outside StockListContextProvider.
  const matchedSupplierParams = useMatch(ORDER_BUYER.pattern);

  const { user } = useSellarUser();
  const supplierUrlIdOrId =
    matchedSupplierParams?.params.supplierIdOrUrlId ?? supplierId;
  const supplierQuery = useSupplier(supplierUrlIdOrId, {
    enabled: !!supplierUrlIdOrId,
    refetchOnWindowFocus: false,
  });

  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const [searchParams] = useSearchParams();
  const { step, basketTotal } = useSignupContext();
  const [unAuthorisedUserPostcode] = useLocalStorage<string | undefined>(
    "unauthorisedUserPostcode",
    undefined,
  );

  const showSellarCreditOnboardingParam = searchParams.get(
    "showSellarCreditOnboarding",
  );

  const storefrontLink =
    searchParams.get("storefrontLink") || searchParams.get("priceTierLink");

  const supplierStorefront = useSupplierStorefrontV2(
    supplierQuery.data?.id ?? 0,
    {
      StorefrontLink: storefrontLink ?? undefined,
      UnAuthoriseUserPostcode: unAuthorisedUserPostcode,
    },
    {
      enabled: !!supplierQuery.data && (!!unAuthorisedUserPostcode || !!user),
      refetchOnWindowFocus: false,
    },
  );

  const renderSupplierInfo = () => {
    switch (step?.name) {
      case SignupFlowStep.MagicLinkSendStep.name:
        return <VerificationSupplierInfo />;
      case SignupFlowStep.SignupDetails.name:
        return <SignupDetailsSupplierInfo />;
      case SignupFlowStep.OptInMarketingStep.name:
        return <MarketingPreferencesSupplierInfo />;
      case SignupFlowStep.BuyerCreationStep.name:
        return <BusinessMatchSupplierInfo />;
      case SignupFlowStep.CreditEligibleStep.name:
        return <SellarCreditEligibleInfo />;
      case SignupFlowStep.ConfirmationStep.name:
        return <ConfirmationSupplierInfo />;
      default:
        return (
          <>
            {supplierQuery.data ? (
              <StorefrontSignupDialogSupplierInfo
                className={"h-full bg-zinc-100"}
                supplier={supplierQuery.data}
              />
            ) : (
              <VerificationSupplierInfo />
            )}
          </>
        );
    }
  };

  return (
    <Dialog
      active={active}
      fullHeight={true}
      className={"p-0 lg:max-h-[780px] lg:max-w-fit"}
    >
      <DialogContent
        className={twMerge(
          "m-auto grid h-full w-full items-center p-0 lg:grid-cols-12 xl:w-[1000px]",
        )}
      >
        <>
          <div className="col-span-6 flex h-full flex-col content-center justify-center overflow-auto p-4 md:p-8 lg:w-full">
            {(supplierQuery.isLoading || supplierStorefront.isLoading) &&
            step?.name === SignupFlowStep.EmailStep.name ? (
              <>
                <SpinnerLegacy />
              </>
            ) : (
              <UserSignupFlow
                supplier={supplierQuery.data}
                hasPriceTier={!!supplierStorefront.data?.PriceTier?.id}
              />
            )}
          </div>
          {isDesktop && (
            <>
              {!!basketTotal || basketTotal === 0 ? (
                <StorefrontSignupDialogBasketInfo
                  className={"h-full bg-zinc-100"}
                  supplier={supplierQuery.data}
                />
              ) : (
                <>
                  {showSellarCreditOnboardingParam ? (
                    <SellarCreditSecondaryCardDesktop />
                  ) : (
                    renderSupplierInfo()
                  )}
                </>
              )}
            </>
          )}
        </>
      </DialogContent>
    </Dialog>
  );
};

const SignupDetailsSupplierInfo = () => {
  const userImages = [
    "https://sellar.imgix.net/sellar-image-assets/app-assets/user-profile-pic-1.png",
    "https://sellar.imgix.net/sellar-image-assets/app-assets/user-profile-pic-2.png",
    "https://sellar.imgix.net/sellar-image-assets/app-assets/user-profile-pic-3.png",
    "https://sellar.imgix.net/sellar-image-assets/app-assets/user-profile-pic-4.png",
    "https://sellar.imgix.net/sellar-image-assets/app-assets/user-profile-pic-5.png",
  ];
  return (
    <div
      className={twMerge(
        "col-span-6 flex h-full flex-col items-center bg-zinc-100",
      )}
    >
      <div
        className={twMerge(
          "flex flex-col items-center justify-center gap-2 lg:m-auto",
        )}
      >
        <ProfilePictureCollection className="flex flex-row">
          {userImages.map((userImage, index) => (
            <ProfilePictureCollectionImage
              key={index}
              imageUrl={userImage}
              name={"user-image"}
              className={"border-white"}
            />
          ))}
        </ProfilePictureCollection>
        <p
          className={"px-12 text-center text-base font-semibold text-zinc-600"}
        >
          Build relationships with suppliers by ordering direct with real-time
          messaging
        </p>
      </div>
    </div>
  );
};

const BusinessMatchSupplierInfo = () => {
  const placeImages = [
    "https://sellar.imgix.net/sellar-image-assets/app-assets/place-1.png",
    "https://sellar.imgix.net/sellar-image-assets/app-assets/place-2.png",
    "https://sellar.imgix.net/sellar-image-assets/app-assets/place-3.png",
  ];
  return (
    <div
      className={twMerge(
        "col-span-6 flex h-full flex-col items-center bg-zinc-100",
      )}
    >
      <div
        className={twMerge(
          "flex flex-col items-center justify-center gap-2 lg:m-auto",
        )}
      >
        <ProfilePictureCollection className="flex flex-row">
          {placeImages.map((placeImage, index) => (
            <div className="relative" key={index}>
              <ProfilePictureCollectionImage
                imageUrl={placeImage}
                name={"user-image"}
                className={"h-16 w-16 border-white"}
              />
              <div className="absolute inset-0 z-10 flex items-center justify-center">
                <Icon
                  size={1.5}
                  className={"text-white opacity-85"}
                  icon={"MAP_MARKER"}
                />
              </div>
            </div>
          ))}
        </ProfilePictureCollection>
        <p
          className={"px-12 text-center text-base font-semibold text-zinc-600"}
        >
          Get orders delivered from anywhere in the UK
        </p>
      </div>
    </div>
  );
};

const VerificationSupplierInfo = () => {
  const supplierLogos = [
    "https://sellar.imgix.net/users/RqTqjd6RhYcosrIQtdCM1vyczzT2/bc78a5f3-56d7-4744-9f0b-44736c2d5809.png",
    "https://sellar.imgix.net/users/5Oc2OEoE5Afw40U2EAP0Xhtkkwn1/c36d70e1-1e5c-48a2-bb50-2eacfddf4bfe.jpg",
    "https://sellar.imgix.net/users/rwJpFt8z1BZGp2r8WuskgnjH5x22/dd3aad30-fec5-4bdf-a3b3-f4c51422aacb.jpeg",
    "https://sellar.imgix.net/users/rwJpFt8z1BZGp2r8WuskgnjH5x22/4817227a-ecb8-4351-8f4c-c27a51a0873f.png",
    "https://sellar.imgix.net/users/vNo5gfW9bZW8VZxAAaDBiHo1gfl2/dd9bbbe4-d802-43f9-91fc-ced5bbde985b.jpg",
    "https://sellar.imgix.net/salt/salt-logo.png",
    "https://sellar.imgix.net/users/rwJpFt8z1BZGp2r8WuskgnjH5x22/0cd614df-d413-4899-84b8-352dc9ce242a.jpg",
    "https://sellar.imgix.net/users/rwJpFt8z1BZGp2r8WuskgnjH5x22/0f72230b-a092-4921-bfa9-f30105b27ee0.png",
    "https://sellar.imgix.net/users/rwJpFt8z1BZGp2r8WuskgnjH5x22/c7983039-c1de-4f9e-9091-7c04bc9d9707.png",
    "https://sellar.imgix.net/users/rwJpFt8z1BZGp2r8WuskgnjH5x22/95bbff24-4736-4293-a969-8e4770da5ca3.png",
    "https://sellar.imgix.net/users/MgDXDjKvqsRm5L7sGUxza6lZ3h52/28c46eda-37dd-4ce7-b4dd-55acb3985871.png",
    "https://sellar.imgix.net/users/rDTwDNm7nbbNSpIHyBwW7pJJ63F3/543e3d35-0e24-4018-acbe-ee81280ec95e.jpg",
    "https://sellar.imgix.net/users/QgTB8qfzBvMCrFZI9CcQTwS2kgR2/461f0ec5-8bd7-45e2-b830-f1636c41fc51.png",
    "https://sellar.imgix.net/users/H41FZJnanpWdp8jA91WnPDq60Xw1/b943112d-1acb-4946-8d8c-13249f47d808.png",
    "https://sellar.imgix.net/users/rwJpFt8z1BZGp2r8WuskgnjH5x22/dfbd8dc3-b126-498d-9a8f-1d92feeac5a4.jpg",
    "https://sellar.imgix.net/users/6tuvxBYb16PWdKWlyQQAGgwPFvE2/b243f552-680b-4d27-b4be-68f9a68a7320.png",
    "https://sellar.imgix.net/users/c7BqpUbhnFS9iKHcnrgwt0j8Pzt2/de57761b-b732-4e57-bb2c-2c3f87857dc7.jpg",
    "https://sellar.imgix.net/mondo-brewing/Mondo-logo.png",
    "https://sellar.imgix.net/users/ORgTQlNQ9dWIgthCna2ikuBe8RD3/49e4352f-3504-4d4f-9c76-7bc8632cd7f8.png",
    "https://sellar.imgix.net/users/rwJpFt8z1BZGp2r8WuskgnjH5x22/25f51b2e-d081-44f0-8c55-2e5fc3aa238d.png",
  ];
  const [randomSupplierLogos] = useState(() =>
    supplierLogos.toSorted(() => 0.5 - Math.random()).slice(0, 5),
  );
  return (
    <div
      className={twMerge(
        "col-span-6 flex h-full flex-col items-center bg-zinc-100",
      )}
    >
      <div
        className={twMerge(
          "flex flex-col items-center justify-center gap-2 lg:m-auto",
        )}
      >
        <ProfilePictureCollection className="flex flex-row">
          {randomSupplierLogos.map((supplierLogo, index) => (
            <ProfilePictureCollectionImage
              key={index}
              imageUrl={supplierLogo}
              name={"user-image"}
              className={"border-white bg-white"}
            />
          ))}
        </ProfilePictureCollection>
        <p
          className={"px-12 text-center text-base font-semibold text-zinc-600"}
        >
          One account for 100s of the UK's best craft drinks providers
        </p>
      </div>
    </div>
  );
};

const SellarCreditSecondaryCardDesktop = () => {
  return (
    <div
      className={twMerge(
        "col-span-6 flex h-full flex-col items-center justify-center bg-zinc-100",
      )}
    >
      <div className="flex flex-col items-center gap-6">
        <img
          src="https://sellar.imgix.net/sellar-image-assets/sellar-credit/sc-invoice.svg"
          className="w-[190px]"
        />
        <p className="max-w-[70%] text-center text-base font-semibold text-zinc-600">
          With Sellar Credit you can get up to 60-day terms with no fees or
          interest and one payment for all orders
        </p>
      </div>
    </div>
  );
};

const MarketingPreferencesSupplierInfo = () => {
  const productImages = [
    "https://sellar.imgix.net/users/rwJpFt8z1BZGp2r8WuskgnjH5x22/0b3af799-26a1-47da-a435-12397a85da02.png",
    "https://sellar.imgix.net/users/RqTqjd6RhYcosrIQtdCM1vyczzT2/6af67077-2f3c-4d36-9900-c7c7d59d3fa1.png",
    "https://sellar.imgix.net/users/5Oc2OEoE5Afw40U2EAP0Xhtkkwn1/be70dc0a-2a53-4abd-9398-bceb7a5048c0.png",
    "https://sellar.imgix.net/users/rwJpFt8z1BZGp2r8WuskgnjH5x22/72a7ad52-f30a-45dd-822e-cff25835af00.png",
    "https://sellar.imgix.net/users/tRgf5n5XfkbcW87NVH9w8rv3GY23/a1786a46-c756-47bf-ab84-23ae90b80b82.png",
    "https://sellar.imgix.net/users/HE3bxFemJnOrgJ2ZJdMts89SVfR2/e03d4db1-6c9a-493d-8805-fe10c02808c5.png",
    "https://sellar.imgix.net/users/4VLjV79bW7dDz0CrORXvc8MX2263/6e371c37-505f-4eda-8da5-41df6d9e1ab6.png",
    "https://sellar.imgix.net/users/9kwBFTJbYiVHGuXrzTes3XbQvAk2/1b9fba51-270c-440a-96a8-dd0504ac00cf.jpg",
    "https://sellar.imgix.net/users/K9659qnUOEcKnyAvah8VxBynCLF3/8383e4a4-518d-4011-9dbd-4ea3e9435535.png",
    "https://sellar.imgix.net/users/rwJpFt8z1BZGp2r8WuskgnjH5x22/8b0c3350-377c-419c-824a-1568c8a79f0f.png",
    "https://sellar.imgix.net/users/MgDXDjKvqsRm5L7sGUxza6lZ3h52/f7e58e49-e051-4209-be62-6881eb2a9df2.png",
    "https://sellar.imgix.net/users/rDTwDNm7nbbNSpIHyBwW7pJJ63F3/117c388e-01db-4eb0-8571-1d5e49aebbe9.jpg",
    "https://sellar.imgix.net/users/NY8BX5AfbyaMrRaZ1RC1PfqL4eg2/41e7cd26-35b6-47f5-8a29-18a0fb0ad0c6.png",
    "https://sellar.imgix.net/users/H41FZJnanpWdp8jA91WnPDq60Xw1/5abbf9f3-4ab7-4456-99b9-0611d2eb13b5.png",
    "https://sellar.imgix.net/users/rwJpFt8z1BZGp2r8WuskgnjH5x22/2dc1dc68-c083-4c16-8073-237a04f5dfe5.jpg",
    "https://sellar.imgix.net/users/6tuvxBYb16PWdKWlyQQAGgwPFvE2/e2b1004a-8cc7-4a4b-9c2b-d3e51b25e75e.png",
    "https://sellar.imgix.net/users/gMhcCbvMitXeW844CUdkIBO9gia2/dfbda05d-2738-4827-a39f-97b443056b42.png",
    "https://sellar.imgix.net/users/OwSYRi2PFAVghRFE1DnVniwcHVA2/Road%20Soda%20-%20New%20England%20Pale%20-%204.8%25.png",
    "https://sellar.imgix.net/users/ORgTQlNQ9dWIgthCna2ikuBe8RD3/fccc9c9d-c5d8-4604-8c7c-05f389d22ad5.png",
    "https://sellar.imgix.net/users/X8KEnlMPmxcWuf1XsZQCir8Ml7P2/e7c65237-dad1-4694-8c0f-6cc4cacd204d.jpg",
  ];
  const [randomProductImages] = useState(() =>
    productImages.toSorted(() => 0.5 - Math.random()).slice(0, 3),
  );
  return (
    <div
      className={twMerge(
        "col-span-6 flex h-full flex-col items-center bg-zinc-100",
      )}
    >
      <div
        className={twMerge(
          "flex flex-col items-center justify-center gap-2 lg:m-auto",
        )}
      >
        <ProfilePictureCollection className="flex flex-row">
          {randomProductImages.map((productImage, index) => (
            <ProfilePictureCollectionImage
              key={index}
              imageUrl={productImage}
              name={"user-image"}
              className={"h-16 w-16 border-white"}
            />
          ))}
        </ProfilePictureCollection>
        <p
          className={"px-12 text-center text-base font-semibold text-zinc-600"}
        >
          Be the first to know about new suppliers, special offers and the
          hottest new releases
        </p>
      </div>
    </div>
  );
};

const ConfirmationSupplierInfo = () => {
  const sellarLogoUrl =
    "https://sellar.imgix.net/sellar-image-assets/app-assets/sellar-logo-new.png";
  return (
    <div
      className={twMerge(
        "col-span-6 flex h-full flex-col items-center bg-zinc-100",
      )}
    >
      <div
        className={twMerge(
          "flex flex-col items-center justify-center gap-2 lg:m-auto",
        )}
      >
        <ProfilePicture
          logoUrl={sellarLogoUrl}
          size={"large"}
          className={"bg-zinc-100"}
        />
        <p
          className={"px-12 text-center text-base font-semibold text-zinc-600"}
        >
          Welcome to Sellar, the best craft drinks trade ordering experience
        </p>
      </div>
    </div>
  );
};

const SellarCreditEligibleInfo = () => {
  const sellarLogoUrl =
    "https://sellar.imgix.net/sellar-image-assets/app-assets/sellar-logo-new.png";
  return (
    <div
      className={twMerge(
        "col-span-6 flex h-full flex-col items-center bg-zinc-100",
      )}
    >
      <div
        className={twMerge(
          "flex flex-col items-center justify-center gap-2 lg:m-auto",
        )}
      >
        <ProfilePicture
          logoUrl={sellarLogoUrl}
          size={"large"}
          className={"bg-zinc-100"}
        />
        <p
          className={"px-12 text-center text-base font-semibold text-zinc-600"}
        >
          One monthly payment, up to 60-day terms, no fees
        </p>
      </div>
    </div>
  );
};
